import ActionsMenu from '../../../../../components/ActionsMenu';
import { CombinationGenerator } from './CombinationGenerator';
import { useToolAccess } from '../../../../../tools/ToolAccessContext';

export function FeatureProperties({
    activeFeature,
    isEditingFeatureName,
    editFeatureNameText,
    setEditFeatureNameText,
    setIsEditingFeatureName,
    isSavingFeatureName,
    isSystem,
    setIsSystem,
    isOutput,
    setIsOutput,
    customAttrs,
    savingFeature,
    suggestions,
    familyId,
    features = [],
    onRenameFeatureSubmit,
    onAddAttrRow,
    onRemoveAttrRow,
    onAttrChange,
    onDeleteFeature,
    onClearSelection,
    onCombinationsGenerated,
    showActionBar = true
}) {
    const { canEditCurrent } = useToolAccess();
    return (
        <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '1rem', minWidth: 0 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border)', paddingBottom: '0.75rem', marginBottom: '0.25rem' }}>
                {isEditingFeatureName ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <input 
                            className="input-field" 
                            value={editFeatureNameText} 
                            onChange={e => setEditFeatureNameText(e.target.value)} 
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    onRenameFeatureSubmit(e);
                                }
                            }}
                            style={{ fontSize: '0.9rem', fontWeight: 'bold', padding: '0.25rem 0.5rem', margin: 0, width: '200px', background: 'var(--bg-primary)' }}
                            autoFocus
                            required
                        />
                        <button type="button" disabled={isSavingFeatureName} onClick={onRenameFeatureSubmit} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--success)', padding: '0.25rem' }} title="Save Feature Name">
                            <i className="fa-solid fa-check" style={{ fontSize: '1rem' }}></i>
                        </button>
                        <button type="button" onClick={() => { setIsEditingFeatureName(false); setEditFeatureNameText(activeFeature.name); }} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--error)', padding: '0.25rem' }} title="Cancel">
                            <i className="fa-solid fa-xmark" style={{ fontSize: '1rem' }}></i>
                        </button>
                    </div>
                ) : (
                    <h4 style={{ margin: 0, fontSize: '1rem', fontWeight: 'bold', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '0.35rem', minWidth: 0 }}>
                        <i className="fa-solid fa-tags" style={{ color: 'var(--accent)' }}></i>
                        {activeFeature.name}
                    </h4>
                )}
                {!isEditingFeatureName && canEditCurrent && (
                    <ActionsMenu
                        label="Feature actions"
                        items={[
                            ...(canEditCurrent ? [{
                                label: 'Rename Feature',
                                icon: 'fa-solid fa-pen',
                                onSelect: () => {
                                    setEditFeatureNameText(activeFeature.name);
                                    setIsEditingFeatureName(true);
                                }
                            },
                            {
                                label: 'Delete Feature',
                                icon: 'fa-solid fa-trash-can',
                                danger: true,
                                onSelect: onDeleteFeature
                            }] : [])
                        ]}
                    />
                )}
            </div>

            <label style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                <input type="checkbox" checked={Boolean(isSystem)} onChange={(e) => setIsSystem(e.target.checked)} />
                Hidden system feature (rules pick the value)
            </label>

            <label style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                <input type="checkbox" checked={Boolean(isOutput)} onChange={(e) => setIsOutput(e.target.checked)} />
                Output feature (rules calculate the value)
            </label>

            <p style={{ margin: 0, fontSize: '0.7rem', color: 'var(--text-secondary)' }}>
                Labels, images, and controls are edited in Release Master.
            </p>

            {isSystem && (
                <CombinationGenerator
                    familyId={familyId}
                    featureId={activeFeature.id}
                    featureName={activeFeature.name}
                    features={features}
                    onGenerated={onCombinationsGenerated}
                />
            )}

            <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.35rem' }}>
                    <label style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-secondary)' }}>Custom Attributes</label>
                    <button 
                        type="button" 
                        onClick={onAddAttrRow}
                        className="btn-primary"
                        style={{ fontSize: '0.65rem', padding: '0.15rem 0.4rem', background: 'var(--text-primary)', color: 'var(--bg-primary)' }}
                    >
                        <i className="fa-solid fa-plus"></i> Add
                    </button>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                    {Array.isArray(customAttrs) && customAttrs.map((attr, idx) => (
                        <div key={idx} style={{ display: 'flex', gap: '0.35rem' }}>
                            <div style={{ flex: 1 }}>
                                <input 
                                    type="text" 
                                    className="input-field" 
                                    placeholder="Key" 
                                    value={attr.key}
                                    onChange={e => onAttrChange(idx, 'key', e.target.value)}
                                    style={{ margin: 0, padding: '0.25rem 0.4rem', fontSize: '0.75rem' }}
                                    required
                                />
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.15rem', marginTop: '0.15rem' }}>
                                    {suggestions.map(sug => (
                                        <button 
                                            key={sug}
                                            type="button"
                                            onClick={() => onAttrChange(idx, 'key', sug)}
                                            style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)', fontSize: '0.55rem', padding: '1px 2px', borderRadius: '2px', cursor: 'pointer' }}
                                        >
                                            {sug}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <input 
                                type="text" 
                                className="input-field" 
                                placeholder="Value" 
                                value={attr.value}
                                onChange={e => onAttrChange(idx, 'value', e.target.value)}
                                style={{ flex: 1, margin: 0, padding: '0.25rem 0.4rem', fontSize: '0.75rem' }}
                                required
                            />
                            <button 
                                type="button" 
                                onClick={() => onRemoveAttrRow(idx)}
                                className="btn-secondary"
                                style={{ padding: '0.25rem 0.4rem', color: 'var(--error)' }}
                            >
                                <i className="fa-solid fa-trash-can" style={{ fontSize: '0.75rem' }}></i>
                            </button>
                        </div>
                    ))}
                </div>
            </div>

            {showActionBar && (
            <div style={{ borderTop: '1px solid var(--border)', paddingTop: '0.85rem', marginTop: 'auto', display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                <button 
                    type="button" 
                    className="btn-secondary" 
                    onClick={onClearSelection}
                    style={{ fontSize: '0.75rem', padding: '0.35rem 0.75rem' }}
                >
                    Clear
                </button>
                <button 
                    type="submit" 
                    className="btn-primary" 
                    disabled={savingFeature || !canEditCurrent}
                    style={{ background: 'var(--text-primary)', color: 'var(--bg-primary)', fontSize: '0.75rem', padding: '0.35rem 0.75rem' }}
                >
                    {savingFeature ? 'Saving...' : 'Save Settings'}
                </button>
            </div>
            )}
        </div>
    );
}
